import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Mail, Lock, User, Phone, Globe, MapPin, ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/Footer';

import { countries } from '../utils/countries';

const Signup = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: location.state?.email || '',
    phone: '',
    country: '',
    city: '',
    password: '',
    confirmPassword: '',
    termsAccepted: false
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.email) {
      setFormData(prev => ({ ...prev, email: location.state.email }));
    }
  }, [location.state]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!formData.country) {
      setError('Please select a country');
      return;
    }

    if (!formData.termsAccepted) {
      setError('You must accept the Terms and Services');
      return;
    }

    setIsLoading(true);
    try {
      const { confirmPassword, ...otherData } = formData;
      const signupData = {
        ...otherData,
        password_confirmation: confirmPassword
      };
      
      const result = await signup(signupData);
      if (result.success) {
        navigate('/onboarding', { state: { from: location.state?.from }, replace: true });
      } else {
        if (result.errors) {
          const errorMessages = Object.values(result.errors).flat();
          setError(errorMessages[0] || result.message || 'Signup failed');
        } else {
          setError(result.message || 'Signup failed');
        }
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <div className="flex-1 flex flex-col items-center justify-center p-4 py-12">
        <div className="max-w-2xl w-full mb-8">
          <Link 
            to="/login" 
            state={{ from: location.state?.from }}
            className="inline-flex items-center gap-2 text-gray-500 hover:text-brand-indigo font-bold text-sm transition-all mb-6 group"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            Back to Login
          </Link>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-8 md:p-12 rounded-3xl border border-gray-100 shadow-2xl shadow-indigo-100/30"
          >
            <div className="flex flex-col items-center md:flex-row md:items-center gap-6 mb-10 text-center md:text-left">
              <div className="w-16 h-16 bg-brand-indigo rounded-2xl flex items-center justify-center text-white shadow-xl shadow-brand-indigo/20 flex-shrink-0">
                <Shield size={32} />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Create Account</h1>
                <p className="text-gray-500 font-medium">Join Chooziq and start collaborating today.</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input 
                    name="firstName"
                    type="text" 
                    placeholder="First Name" 
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-bold transition-all"
                  />
                </div>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input 
                    name="lastName"
                    type="text" 
                    placeholder="Last Name" 
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-bold transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input 
                    name="email"
                    type="email" 
                    placeholder="Email address" 
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-bold transition-all"
                  />
                </div>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input 
                    name="phone"
                    type="tel" 
                    placeholder="Phone Number" 
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-bold transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <select 
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-bold transition-all appearance-none"
                  >
                    <option value="" disabled>Select Country</option>
                    {countries.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input 
                    name="city"
                    type="text" 
                    placeholder="State / City" 
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-bold transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input 
                    name="password"
                    type="password" 
                    placeholder="Password" 
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-bold transition-all"
                  />
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input 
                    name="confirmPassword"
                    type="password" 
                    placeholder="Confirm Password" 
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-bold transition-all"
                  />
                </div>
              </div>
              
              {error && <p className="text-red-500 text-sm font-bold px-2">{error}</p>}

              <div className="flex items-center gap-2 px-2 text-xs text-gray-500 font-medium">
                <input 
                  id="termsAccepted"
                  name="termsAccepted"
                  type="checkbox" 
                  checked={formData.termsAccepted}
                  onChange={handleChange}
                  required 
                  className="w-4 h-4 rounded border-gray-300 text-brand-indigo focus:ring-brand-indigo/20 cursor-pointer" 
                />
                <label htmlFor="termsAccepted" className="cursor-pointer">
                  By signing up, you agree to our <Link to="/terms" className="text-brand-indigo hover:underline">Terms</Link> and <Link to="/privacy" className="text-brand-indigo hover:underline">Privacy Policy</Link>.
                </label>
              </div>

              <button 
                type="submit"
                disabled={isLoading}
                className={`w-full ${isLoading ? 'bg-brand-indigo/70 cursor-not-allowed' : 'bg-brand-indigo hover:bg-brand-indigo/90'} text-white font-bold py-4 rounded-2xl shadow-xl shadow-brand-indigo/10 flex items-center justify-center gap-2 transition-all active:scale-[0.98]`}
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    Create My Account
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 pt-8 border-t border-gray-50 text-center">
              <p className="text-sm text-gray-500 font-bold">
                Already have an account? 
                <Link to="/login" state={{ from: location.state?.from }} className="text-brand-indigo hover:underline ml-1">Sign In</Link>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Signup;
